import { defineStore } from 'pinia';
import dummyLead from '@/assets/data/dummyLead-complete';
import { isFunction, isString } from '@vue/shared';
import { merge } from 'lodash';
// import { qualifiedSectionKeys } from '@/store/QualificationStore'
import { parsePhoneNumber } from 'libphonenumber-js';
import { getInterestDefinition, m29Interests } from '@/rules/InterestDefinitions';
import { getSocialMinimum140ForM29 } from '@/services/SocialMinimumService';
import { useInterfaceStore } from './UiStore';
import { useQualificationStore } from './QualificationStore';
import { getLeadSource } from '@/helpers/M29MunicipalitySrc';

import postcodeAppointmentConfig from '@/config/postcodeAppointmentConfig';


export const useLeadStore = defineStore({
  // id is required so that Pinia can connect the store to the devtools
  id: 'LeadStore',
  state: () => ({
    meta: {
      // QuickCheckVersion keeps track of the version that's being used by the API.
      // History:
      // - 1.0: Initial version of the quickcheck
      // - 1.1: Splitted Quickcheck in 2 phases. Changed steps for the NijBegun M29 (10-11-2025)
      // - 1.2: Added the new phase 2 questions (09-12-2025)
      // - 1.3: Combined d2d with normal Quickcheck. Added logic based on host and lead source (23-12-2025)
  
      QuickcheckVersion: '1.3',
      PartnerCoupon: null,
      CallBackRequested: false,
      InternalUserId: null, // Salesforce UserId when used by employee
      ReturnUrl: null, // url to return
      CallbackUrl: null, // id to supply when submitting result
      PortalId: null, // id in klantenportaal
      LowQualitySource: true,
      LeadSource: getLeadSource(),
      AllInterestsEnabled: true,
      InterestWhiteList: ['Floor', 'Cavity Wall', 'Roof', 'Glazing', 'Ventilation', 'Isolatieplan Maatregel 29', 'Ondersteuning Nij Begun'],
      SendConfirmation: true,
      PostcodeCallbackDate: null,
      LeadStatus: null,
      LeadDisqualificationReason: null,
      VisitedViews: [],
      HadAdviesGesprek: false,
      StatusIsolatieplan: null,
      CookiesAccepted: null,
      AdviseurLite: false,
    },
    bellerLoper: {
      Naam: null,
    },
    interests: {
      Interests: [],
      IsoplanInterest: null,
      InterestNotes: {},
      UrgencyRating: null,
      Motivation: null,
    },
    wishes: {
      Wishes: [],
      WishNotes: {},
    },
    executedMeasures: {
      ExecutedMeasures: [],
      ExecutedMeasuresNotes: {},
    },
    currentInsulation: {
      FloorInsulated: null,
      Participate: null,
      FloorInsulationImpossible: null,
      InterestedInFutureCampaign: null,
      InterestedInIsolatiePlan: null,
      ConfirmNoParticipation: null,
    },
    contact: {
      Salutation: null,
      FirstName: null,
      LastName: null,
      Email: null,
      Phone: null,
    },
    address: {
      id: null,
      eligible: null,
      insert_tag: null,
      eligible_handyman: null,
      Street: null,
      StreetNumber: null,
      Street_number_letter__c: null,
      Street_number_addition__c: null,
      PostalCode: null,
      City: null,
      Municipality: null,
      BAG_Object_ID__c: null,
      BAG_nummeraanduidingIdentificatie__c: null,
      Energielabel_datum__c: null,
      Energielabel__c: null,
      BAG_openbareRuimteNaam__c: null,
      BAG_korteNaam__c: null,
      BAG_woonplaatsNaam__c: null,
      BAG_huisnummer__c: null,
      BAG_huisletter__c: null,
      BAG_huisnummertoevoeging__c: null,
      BAG_postcode__c: null,
      BAG_openbareRuimteIdentificatie__c: null,
      BAG_woonplaatsIdentificatie__c: null,
      BAG_adresregel5__c: null,
      BAG_adresregel6__c: null,
      BAG_typeAdresseerbaarObject__c: null,
      BAG_oppervlakte__c: null,
      BAG_gebruiksdoelen__c: null,
      BAG_oorspronkelijkBouwjaar__c: null,
      BAG_pandStatussen__c: null,
      BAG_self_link__c: null,
      BAG_openbareRuimte_link__c: null,
      BAG_nummeraanduiding_link__c: null,
      BAG_woonplaats_link__c: null,
      BAG_adresseerbaarObject_link__c: null,
      BuildingCluster__c: null,
      EP_postcode__c: null,
      EP_afschrift__c: null,
      EP_gebouwtype__c: null,
      EP_huisletter__c: null,
      EP_huisnummer__c: null,
      EP_gebouwklasse__c: null,
      EP_berekeningstype__c: null,
      EP_certificaathouder__c: null,
      EP_metingGeldigTot__c: null,
      EP_detailaanduiding__c: null,
      EP_bagVerblijfsobjectId__c: null,
      EP_huisnummertoevoeging__c: null,
      EP_isVereenvoudigdLabel__c: null,
      EP_isOpBasisVanReferentiegebouw__c: null,
      buurtnaam: null,
      wijknaam: null,
      woz_eigenaar_soort: null,
      woz_gebruiker_soort: null,
      woz_typologie: null,
      woz_waarde_ozb: null,

      inVersterkingsScope: null,
      created_at: null,
      last_updated: null,
      last_EP_fetch: null,
      last_BAG_fetch: null,
      m29_100pct: null,
      assigned_partner: null,
      pc_GemNaam: null,
      pc_AantalAdressen: null,
      pc_m29_open_from_string: null,
      pc_m29_open_from_datetime: null,
      pc_m29_municipality: null,
      eligible_for_ms: null,
      gem_id: null,
      gem_NIP_available: null,
      gem_NIP_landingpage_url: null,
      gem_NIP_legal_url: null,
      gem_NIP_amount_desc_short: null,
      gem_NIP_subsidy_amount: null,
      gem_NIP_subsidy_pct: null,
      gem_NIP_max_income_desc: null,
      gem_NIP_max_income: null,
      gem_NIP_income_ref_year: null,
      gem_NIP_max_energielabel: null,
      gem_NIP_max_WOZ: null,
      gem_NIP_WOZ_peiljaar: null,
      gem_NIP_max_construction_year: null,
      m29_ontzorging_door_BvV_toegestaan: null,
      eligible_for_isoplan_plus: null
    },
    
    otherSubsidies: {
      interestedInOtherSubsidies: null,
      subsidyEligibility: [],
      twkEligibleSubsidies: [],
      is55OrOlder: null,
      energyLabelDOrLower: null,
      hasTwoPoorInsulatedParts: null,
      energyLabelDOrLowerOrTwoPoorInsulatedParts: null,
      vhfEnergyLabelBOrLower: null,
      vhfNeedsIsolationStandardMeasures: null,
      subsidy: {
        measuresExecuted: null,
        executedAt: null,
        twkEligibility: {},
      },
      woz: {
        woz_waarde_volgens_bewoner: null,
      },
    },
    ownership: {
      PropertyOwner: null,
      OwnerIsResident: null,
      OwnerPermission: null,
      OwnerName: null,
      OwnerPhone: null,
      OwnerEmail: null,
      HoaMember: null,
      HoaPermission: null,
      HoaRepName: null,
      HoaRepPhone: null,
      HoaRepEmail: null,
    },
    registration: {
      WantsToRegisterAnyway: null,
      RegisterAnywayReason: null,
    },
    intentions: {
      Termijn: null, // string, op welke termijn wilt u isoleren: "Zo snel mogelijk", "binnen een half jaar", "later"
      Aanpak: null, // in één keer of maatregel voor maatregel: "Alles in één keer", "Stapsgewijs over langere tijd"
    },
    advice: {
      ConsentForContact: null,
      HelpWithQuotes: null,
      HelpWithVHF: null,
      Maatregel29Support: null
    },
    contactPreferences: {
      PreferredContactTime: 'Geen voorkeur',
      SpeaksDutch: null,
      PreferredLanguage: null,
    },
    financing: {
      snnSubsidyVenV: {
        hulpSnnSubsidies: null,
        InVersterkingVoor2020: null, // added nov 24
        VerouderdVersterkingsAdvies: null, // added nov 24
        RequestedSnn7k: null, // added nov 24
        InVersterking: null, // present since v1
        RequestedSnn10k: null, // present since v1
        WoningInBlokB: null, // added nov 24
        RequestedSnn17k: null, // added nov 24
        RequestedSnn30k: null, // added nov 24

        snnSubsidy4k: {
          HeeftAardbevingsschade: null,
          NietEerderGebruikt: null
        },
      },
      income: {
        ReceivedEnergySupport: null, // energietoeslag ontvangen in 2023 https://www.bwri.nl/energietoeslag-2023-aanvragen
        // leef situatie required
        RequireLeefVorm: null, // leefvorm uitvragen
        CivilStatus: null, // burgerlijke staat
        AgeBracket: null, // leeftijdscategorie
        AgeBracketPartner: null, // leeftijdscategorie partner
        HasChildren: null,
        // statically updated
        CalculatedSocialMinimum: null, // berekend sociaal minimum
        AppliedNettMinimumWages: {}, // aangehouden minimumloon (netto) (object)
        SM_100pct: null, // -100% van sociaal minimum (boolean)
        SM_110pct: null, // -110% van sociaal minimum (boolean)
        SM_125pct: null, // -125% van sociaal minimum (boolean)
        SM_140pct: null, // -140% van sociaal minimum (boolean)
        SM_160pct: null, // -160% van sociaal minimum (boolean)
        SM_180pct: null, // -180% van sociaal minimum (boolean)
        WML_100pct: null, // -100% van minimumloon (boolean)
        // WML_125pct: null, // -125% van minimumloon // these are now calculated dynamically (boolean)
        // WML_140pct: null, // -140% van minimumloon // these are now calculated dynamically (boolean)
        over_WML_140pct: null, // > 140% van minimumloon (boolean)
        ExpectLowerIncomeThisYear: null,
      },
      nip: {
        OverMaxIncome: null, // 125% van minimumloon (2023) (extra naast income categories)
        PermissionToRequestNip: null,
      },
      maatregel29: {
        IncomeOver140pct: null // deprecated, replaced by income categories in .income
      },
      warmtefonds: {
        WantEblWarmtefonds: null, // Energiebespaarflening Warmtefonds
      },
      stimuleringslening: {
        WantStimuleringsleningMG: null, // stimuleringslening Midden-Groningen
      },
      mortgage: {
        HasRaboMortgageAndLabelFG: null,
        WantUpdateMortgage: null
      },
      wantHelpFinancing: null,
    },
    practicalHelp: {
      WantPracticalHelp: null,
      WantPracticalHelpFake: null, // only used to let the user think they get help, but just gets in normal SF list
    },
    property: {
      ConstructionYear: null,
      GasConsumption: null,
      PowerConsumption: null,
      HouseType: null,
      IsMonument: null,
      IsBeschermdGezicht: null,
      InVersterkingsprogramma: null,
      VersterkingsprogrammaUitgevoerd: null
    },
    twk: {
      IntakeMeasuresExecuted: null,
    },
    isolatieplanCheck: {
      Under10kCombined: null,
      ProceedWithoutPlan: null,
      InterstedInWarmtepomp: null,
      IsolatieplanPlus: null,
      IsolatieplanRequested: null,
      IsolatieplanReceived: null,
      bewonerChoosesOwnAdvisor: null,
    },
    intakeCompletion: {
      UserSituation: null,
      UserSituationManuallySet: false,
      Contact: null,
      WillingToSpendBeforeSubsidy: null
    },
    isolatieplan: {
      HasMeasuresForRetroactive: null,
      DeclaredAtOtherSubsidy: null,
      RetroactiveMeasuresTotalAmount: null,
      RetroactiveMeasures: [],
    },
    cavity_wall: {
      general: {
        CavityWallPresent: null,
        FacadeMaterial: null,
      },
      paint: {
        WallsPainted: null,
        AcceptWallRepaint: null,
      },
      stucco: {
        WallsPlastered: null,
        AcceptStuccoRepaint: null,
      },
    },
    floor: {
      general: {
        SpaceBeneathFloor: null,
        CrawlSpaceAccessible: null,
      },
      details: {
        CrawlSpaceHeightCm: null,
        FloorInsulationSide: 'Vloer',
      },
    },
    roof: {
      general: {
        RoofInsulationStatus: null,
        RoofInsulationSide: null,
        // RoofReplacementWithinDecade: null,
        RoofType: null
      },
      attic: {
        AtticPresent: null,
        AccessToRoofInside: null,
        AtticUsage: [],
      },
      finishing: {
        RoofBeamsVisible: null,
        RoofInsideFinishingPresent: null,
        RoofFinishingRemovalPermission: null,
        RoofBeamCoveringRemoved: null,
      },
    },
    glazing: {
      general: {
        IsMonument: null,
        BuildingAccessibleAllround: null,
        CurrentGlazingType: null,
        CurrentGlazingAge: null,
      },
      frame: {
        RebateWidthSufficient: null,
        FrameMaterial: null,
        FrameCondition: null,
      },
      dimensions: {
        Panes: [
          {
            width: null,
            height: null
          }
        ]
      }
    },
    heatpump: {
      HeatingSystem: null,
      HeatpumpFinancing: null
    },
    solar: {
      PlacementLocations: []
    }
  }),
  getters: {
    // utility getters
    $fields(state) {
      return Object.keys(state)
        .filter(function (key) {
          return (
            key.charAt(0) !== '$' &&
            key.charAt(0) !== '_' &&
            !isFunction(state[key])
          );
        })
        .reduce(function (fields, key) {
          fields[key] = state[key];
          return fields;
        }, {});
    },

    // field getters
    interestsArray(state) {
      if (!state.interests || !Array.isArray(state.interests.Interests)) {
        return [];
      }
      return state.interests.Interests;
    },
    getAtticUsage(state) {
      return Array.isArray(state.roof.attic.AtticUsage)
        ? state.roof.attic.AtticUsage
        : [];
    },
    AtticUsedForStorage(state) {
      return state.getAtticUsage.includes('storage');
    },
    AtticUsedForLiving(state) {
      return state.getAtticUsage.includes('living');
    },
    skipRoofFinishing(state) {
      return state.roof.attic.AccessToRoofInside == 'Vlizotrap';
    },
    /**
     * Should only return the following values:
     * enkel-voor-1960
     * enkel-na-1960
     * dubbel-voor-1995
     * dubbel-na-1995
     * null
     */
    GlazingToReplace(state) {
      let type = state.glazing.general.CurrentGlazingType;
      let age = state.glazing.general.CurrentGlazingAge;
      return type && age ? type + '-' + age : null;
    },

    GlazingDimensionsText(state) {
      let text = '';
      let panes = state.glazing.dimensions.Panes;
      if (!Array.isArray(panes) || panes.length < 1) { return text; }
      text = "Afmetingen ruiten (breedte x hoogte): \n";
      // iterate through panes and generate text
      for (let i = 0; i < panes.length; i++) {
        text += "Ruit " + (i+1) + ": " + panes[i].width + ' x ' + panes[i].height + ' cm';
        if (i < panes.length - 1) {
          text += "\n";
        }
      }
      return text;
    },

    QualifiedInterests(state) {
      const selected = Array.isArray(state.interests?.Interests) ? state.interests.Interests : [];
      return selected.filter(v => {
        // Check if it's in standard definitions OR in m29Interests
        return getInterestDefinition(v, true) !== null || m29Interests.includes(v);
      });
    },

    /**
     * returns all qualified interests, and choosing floor or crawl space based on FloorInsulationSide
     * @returns {Array<string>}
     */
    // QualifiedInterests(state) {
    //   let qualifiedSections = qualifiedSectionKeys.value;
    //   if (!Array.isArray(qualifiedSections)) { return []; }
    //   if (qualifiedSections.includes('floor')) {
    //     if (get(state, 'floor.details.FloorInsulationSide', null) == 'Bodem') {
    //       remove(qualifiedSections, function(value) {
    //         return value == 'floor';
    //       })
    //       qualifiedSections.push('crawl_space');
    //     }
    //   }


    //   return qualifiedSections;
    // },

    FormattedPhone(state) {
      if (!state.contact.Phone) { return null; }
      try {
        let phoneNumber = parsePhoneNumber(state.contact.Phone, 'NL');
        return phoneNumber.formatInternational();
      } catch (error) {
        console.error('Error formatting phone number:', error);
        return null;
      }
    },

    BvvGemeenteAllowed(state) {
      if (!state.address || !state.address.Municipality) {
        return false;
      }
      const allowedMunicipalities = ['Het Hogeland', 'Midden-Groningen', 'Oldambt', 'Pekela', 'Stadskanaal', 'Veendam', 'Westerwolde'];
      return allowedMunicipalities.includes(state.address.Municipality);
    },

    PrefilledCalendlyUrl(state) {
      if (state.requiresPersonalFollowUp || !state.contact.Phone || !this.FormattedPhone) return null;
      const uiStore = useInterfaceStore();
      const trimmedFormattedPhone = this.FormattedPhone.replaceAll(' ', '');

      // let url = `https://portal.bureauverduurzamen.nl/belafspraak/lead/${uiStore.uuid}?first_name=${state.contact.FirstName}&last_name=${state.contact.LastName}&email=${state.contact.Email}&phone=${trimmedFormattedPhone}&postcode=${state.address.PostalCode}&huisnummer=${state.address.StreetNumber}`;
      // let url = `https://portal.bureauverduurzamen.nl/belafspraak/advies-nijbegun-30min/${uiStore.uuid}?first_name=${state.contact.FirstName}&last_name=${state.contact.LastName}&email=${state.contact.Email}&phone=${trimmedFormattedPhone}&postcode=${state.address.PostalCode}&huisnummer=${state.address.StreetNumber}`;
      
      const userSituation = state.intakeCompletion.UserSituation;

      const adviseurLite = state.meta.AdviseurLite;

      if (userSituation === 'appointment_isolatieplan') {
        // appointment_isolatieplan
        if (adviseurLite) {
          return `https://calendly.com/verduurzaming/intakegesprek-isolatieplan-nij-begun/?salesforce_uuid=${uiStore.uuid}&first_name=${state.contact.FirstName}&last_name=${state.contact.LastName}&email=${state.contact.Email}&a1=${trimmedFormattedPhone}&a2=${state.address.PostalCode}&a3=${state.address.StreetNumber}`
        } else {
          return `https://portal.bureauverduurzamen.nl/belafspraak/advies-nijbegun-30min/${uiStore.uuid}?first_name=${state.contact.FirstName}&last_name=${state.contact.LastName}&email=${state.contact.Email}&phone=${trimmedFormattedPhone}&postcode=${state.address.PostalCode}&huisnummer=${state.address.StreetNumber}`;
        }
        
      } else if (userSituation === 'quotes_without_plan') {
        // quotes_without_plan
        return `https://calendly.com/verduurzaming/telefonisch-adviesgesprek-nij-begun-10k/?salesforce_uuid=${uiStore.uuid}&first_name=${state.contact.FirstName}&last_name=${state.contact.LastName}&email=${state.contact.Email}&a1=${trimmedFormattedPhone}&a2=${state.address.PostalCode}&a3=${state.address.StreetNumber}`;
      } else {
        //Fallback use the advies-nijbegun-30min url
        return `https://portal.bureauverduurzamen.nl/belafspraak/advies-nijbegun-30min/${uiStore.uuid}?first_name=${state.contact.FirstName}&last_name=${state.contact.LastName}&email=${state.contact.Email}&phone=${trimmedFormattedPhone}&postcode=${state.address.PostalCode}&huisnummer=${state.address.StreetNumber}`;
      }
    },

    NIP_EP_under_max(state) {
      // if specified in config, gem_NIP_max_energielabel
      if (state.address && state.address.gem_NIP_max_energielabel && isString(state.address.Energielabel__c)) {
        return state.address.Energielabel__c.trim() >= state.address.gem_NIP_max_energielabel.trim();
      // else assume D or lower
      } else if (state.address.Energielabel__c == 'D' || state.address.Energielabel__c == 'E' || state.address.Energielabel__c == 'F' || state.address.Energielabel__c == 'G') {
        return true;
      }
      return false;
    },

    NIP_potentially_eligible(state) {
      // ignores whether address is eligible or not
      // if income under max income, or if income is not required
      // if NIP_EP_under_max
      if (state.financing.nip.OverMaxIncome === true) {
        return false;
      }
      if (state.NIP_EP_under_max === false) {
        // return false;
      }
      return true;
    },

    NIP_address(state) {
      // whether NIP is available for address (not taking other factors into account)
      if (!state.address || !state.address.Municipality && !state.address.City) {
        return false;
      } else if (state.address.gem_NIP_available == 1) {
        return true;
      } else {
        return state.address.Municipality == 'Midden-Groningen' || state.address.City == 'Hoogezand';
      }
    },
    Eligible_Handyman(state) {
      if (!state.address || !state.address.Municipality && !state.address.City) {
        return false;
      } else {
        return state.address.Municipality == 'Midden-Groningen' || state.address.City == 'Hoogezand';
      }
    },
    // eligible maatregelstromen
    // true/false/null, best for use in combination with busy state on address page
    Eligible_ms(state) {
      if (state.address.id && state.address.eligible_for_ms) {
        return true;
      } else if (state.address.id && !state.address.eligible_for_ms) {
        return false;
      } else {
        return null;
      }
    },
    // string 'eligible', 'ineligible' or 'incomplete' for use in other pages
    Eligible_ms_status(state) {
      return state.Eligible_ms === true ? 'eligible' : state.Eligible_ms === false ? 'ineligible' : 'incomplete';
    },
    Isolatieplan_required_bouwjaar(state) {
      return state.property.ConstructionYear > 1991;
    },
    isVhfPekela(state) {
      return state.address.insert_tag === 'Pilot Pekela';
    },
    requiresPersonalFollowUp(state) {
      const assignedPartner = (state.address.assigned_partner || '').toString().trim().toLowerCase();
      return state.isVhfPekela || Boolean(state.address.eligible) || assignedPartner === 'bureau voor verduurzamen';
    },
    Isolatieplan_required(state) {
      return state.Isolatieplan_required_bouwjaar || state.property.IsMonument === true || state.isVhfPekela;
    },
    stimuleringslening_address(state) {
      // whether NIP is available for address (not taking other factors into account)
      if (!state.address || !state.address.Municipality && !state.address.City) {
        return false;
      } else {
        return state.address.Municipality == 'Midden-Groningen' || state.address.City == 'Hoogezand';
      }
    },
    
    hasIncomeCategory(state) {
      if (
        state.financing.income.SM_100pct ||
        state.financing.income.SM_110pct ||
        state.financing.income.SM_125pct ||
        state.financing.income.SM_140pct ||
        state.financing.income.SM_160pct ||
        state.financing.income.SM_180pct ||
        state.financing.income.WML_100pct ||
        state.financing.income.WML_125pct ||
        state.financing.income.WML_140pct ||
        state.financing.income.over_WML_140pct
      ) {
        return true;
      }
      return false;
    },

    m29_SM140_amount(state) {
      // alleenstaand =  27062
      // samenwonend =  38130
      return getSocialMinimum140ForM29(state.financing.income.CivilStatus);
    },
    WML_125pct(state) {
      return state.financing.income.SM_140pct && state.financing.income.CivilStatus === 'Alleenstaand' ? true : null;
    },
    WML_140pct(state) {
      return state.financing.income.SM_140pct && state.financing.income.CivilStatus === 'Alleenstaand' ? true : null;
    },
    hasExecutedMeasures(state) {
      return Array.isArray(state.executedMeasures?.ExecutedMeasures) &&
        state.executedMeasures.ExecutedMeasures.length > 0;
    },
    isIsolatieplanPortalLead(state) {
      // return true;
      return state.meta.LeadSource === 'isolatie.nijbegun.nl portaal isolatieplan' || 
             state.meta.LeadSource === 'isolatie.nijbegun.nl portaal isolatieplan - Bosman';
    },
    defaultUserSituation(state) {
      if (state.isIsolatieplanPortalLead) {
        return 'appointment_isolatieplan';
      }

      return null;
    },
    wantsIsolatieplan(state) {
      if (state.Isolatieplan_required) {
        return true;
      }
      // Check based on isolatieplan chosen in one of the paths
      if (state.twk.IntakeMeasuresExecuted === true) {
        return state.isolatieplanCheck.Under10kCombined === false;
      } else if (state.twk.IntakeMeasuresExecuted === false) {
        return state.isolatieplanCheck.ProceedWithoutPlan === false;
      }
      return null;
    },
    LeadStatus() {
      const qualStore = useQualificationStore();
      if (qualStore.global.status === 'disqualified') {
        return 'Niet gekwalificeerd';
      }
      return 'Open';
    },
    LeadDisqualificationReason() {
      if (this.$state.property.ConstructionYear > 2012) {
        return 'Woning voldoet al aan standaard'
      }
      if (this.$state.property.IsMonument === true) {
        return 'Monument';
      }
      if (this.$state.property.InVersterkingsprogramma === true && this.$state.property.VersterkingsprogrammaUitgevoerd === false) {
        return 'Versterkingsopgave';
      }
      return null;
    },
    municipalityHasNoVersterking() {
      const municipalitiesWithoutVersterking = ['Pekela', 'Veendam', 'Westerwolde', 'Stadskanaal'];
      return municipalitiesWithoutVersterking.includes(this.$state.address.Municipality);
    },
  },
  actions: {
    setPracticalHelpRequested(value = true) {
      this.$state.practicalHelp.WantPracticalHelpFake = value;
    },
    loadRemoteData(remoteData = null) {

      if (remoteData) {
        merge(this.$state, remoteData);
      }

      if (this.municipalityHasNoVersterking) {
        this.$state.property.InVersterkingsprogramma = false;
      }

      if (!this.$state.intakeCompletion.UserSituation) {
        this.$state.intakeCompletion.UserSituation = this.defaultUserSituation;
      }

      this.syncLeadStatus();
    },
    loadDummyData() {
      // Object.assign(this.state, dummyLead);
      this.$state = { ...this.$state, ...dummyLead };
      this.syncLeadStatus();
    },
    checkInterest(interestValue = null) {
      const definition = getInterestDefinition(interestValue);
      if (interestValue && definition !== null && !this.$state.interests.Interests.includes(interestValue)) {
        this.$state.interests.Interests.push(interestValue);
        return definition;
      }
      return null;
    },
    setLeadSource(leadSource = null) {
      if (leadSource) {
        const formattedLeadSource = getLeadSource(leadSource);
        this.$state.meta.LeadSource = formattedLeadSource;
      }
    },
    setContactDetailsFromQueryParams(params = {}) {
      if (params.email) {
        this.$state.contact.Email = params.email;
      }
      if (params.phone) {
        this.$state.contact.Phone = params.phone;
      }
      if (params.first_name) {
        this.$state.contact.FirstName = params.first_name;
      }
      if (params.last_name) {
        this.$state.contact.LastName = params.last_name;
      }
      if (params.salutation) {
        this.$state.contact.Salutation = params.salutation;
      }
      if (params.postal_code) {
        this.$state.address.PostalCode = params.postal_code;
      }
      if (params.house_number) {
        this.$state.address.StreetNumber = params.house_number;
      }
    },
    setCookiesAccepted(value = true) {
      if (value === true || value === 'true' || value === 'accepted' || value === 1) {
        this.$state.meta.CookiesAccepted = true;
      } else if (value === false || value === 'false' || value === 'rejected' || value === 0) {
        this.$state.meta.CookiesAccepted = false;
      } else {
        this.$state.meta.CookiesAccepted = null;
      }
    },
    /* backfill pane ids on panes without id, only to be as key attribute on v-for items
    purpose is to ensure correct behaviour when deleting pane in middle of array */
    backfillPaneIds() {
      let panes = this.$state.glazing.dimensions.Panes;
      if (!Array.isArray(this.$state.glazing.dimensions.Panes)) {
        // set to empty array
        this.$state.glazing.dimensions.Panes = [];
        return;
      }
      // iterate through panes and generate ids (new Date().getTime()) if not set
      for (let i = 0; i < panes.length; i++) {
        if (!panes[i].id) {
          panes[i].id = new Date().getTime() + i;
        }
      }
      this.$state.glazing.dimensions.Panes = panes;
    },
    // Calculate and set meta.PostcodeCallbackDate.
    // If the postcode open date is further away than daysUntilShowScheduler,
    // set PostcodeCallbackDate to (openDate - daysBeforeCallbackToForwardOpenDate).
    // Otherwise set to null
    updatePostcodeCallbackDate() {
      if (this.$state.intakeCompletion.UserSituation === 'waiting_postcode') {
        const dateStr = this.$state.address?.pc_m29_open_from_datetime;
        if (!dateStr) {
          this.$state.meta.PostcodeCallbackDate = null;
          return;
        }
        const openDate = new Date(String(dateStr).replace(' ', 'T'));
        if (Number.isNaN(openDate.getTime())) {
          this.$state.meta.PostcodeCallbackDate = null;
          return;
        }

        const now = new Date();
        const diffDays = Math.ceil((openDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)); // Days difference between now and openDate

        const showThresholdDays = Number(postcodeAppointmentConfig.daysUntilShowScheduler) || 0;
        const daysBefore = Number(postcodeAppointmentConfig.daysBeforeCallbackToForwardOpenDate) || 0;

        if (diffDays > showThresholdDays) {
          // More than threshold: schedule callback earlier (openDate - daysBefore)
          const callbackDate = new Date(openDate.getTime() - daysBefore * 24 * 60 * 60 * 1000);
          this.$state.meta.PostcodeCallbackDate = callbackDate.toISOString();
        } else {
          this.$state.meta.PostcodeCallbackDate = null;
        }
      } else {
        this.$state.meta.PostcodeCallbackDate = null;
      }
    },
    syncLeadStatus() {
      this.$state.meta.LeadStatus = this.LeadStatus;
      this.$state.meta.LeadDisqualificationReason = this.LeadDisqualificationReason;
    },
    markViewAsVisited(viewName) {
      if (!this.$state.meta.VisitedViews.includes(viewName)) {
        this.$state.meta.VisitedViews.push(viewName);
      }
    },
  },
});
